import { Box, IconButton, Tooltip } from '@mui/material';
import { useState } from 'react';
import { HelpOutline as QuestionIcon } from '@mui/icons-material';
import { HelpModal } from './index';

interface HelpButtonProps {
  variant?: 'floating' | 'inline' | 'sidebar';
  size?: 'small' | 'medium' | 'large';
  tooltip?: string;
  videoUrl?: string;
  title?: string;
  description?: string;
  isCollapsed?: boolean;
  /** When true, the help modal includes mailto + in-app support ticket (logged-in users). */
  enableSupportSection?: boolean;
}

const HelpButton = ({
  variant = 'floating',
  size = 'medium',
  tooltip = 'Watch tutorial video',
  videoUrl = 'https://www.youtube.com/watch?v=-nV91fjZB30',
  title = 'How to use this application',
  description = 'Watch our comprehensive tutorial to learn how to navigate and use all features of the application.',
  isCollapsed = false,
  enableSupportSection = false,
}: HelpButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return { width: 32, height: 32 };
      case 'large':
        return { width: 48, height: 48 };
      default:
        return { width: 40, height: 40 };
    }
  };

  const getVariantStyles = () => {
    if (variant === 'floating') {
      return {
        position: 'fixed' as const,
        bottom: 24,
        right: 24,
        zIndex: 1000,
        backgroundColor: '#1976d2',
        color: 'white',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        '&:hover': {
          backgroundColor: '#1565c0',
          transform: 'scale(1.05)',
        },
        transition: 'all 0.2s ease-in-out',
      };
    }
    if (variant === 'sidebar') {
      return {
        width: '100%',
        backgroundColor: '#F8F9FA',
        border: '1px solid #E9ECEF',
        borderRadius: '12px',
        padding: '12px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: '#E9ECEF',
        },
      };
    }
    return {
      backgroundColor: 'transparent',
      color: 'rgba(0, 0, 0, 0.6)',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        color: '#1976d2',
      },
    };
  };

  if (variant === 'sidebar') {
    return (
      <>
        <Tooltip title={tooltip} placement="right">
          <Box
            onClick={handleOpenModal}
            sx={{
              ...getVariantStyles(),
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              gap: 1,
            }}
            aria-label="Help"
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                color: 'rgba(0, 0, 0, 0.6)',
                '&:hover': {
                  color: '#1976d2',
                },
              }}
            >
              <QuestionIcon sx={{ fontSize: '20px' }} />
            </Box>
            {!isCollapsed && (
              <Box sx={{ ml: 1 }}>
                <Box
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#495057',
                    lineHeight: 1.2,
                  }}
                >
                  Need Help?
                </Box>
                <Box
                  sx={{
                    fontSize: '12px',
                    color: '#6C757D',
                    lineHeight: 1.2,
                  }}
                >
                  Watch tutorial
                </Box>
              </Box>
            )}
          </Box>
        </Tooltip>
        
        <HelpModal
          open={isModalOpen}
          onClose={handleCloseModal}
          videoUrl={videoUrl}
          title={title}
          description={description}
          enableSupportSection={enableSupportSection}
        />
      </>
    );
  }

  return (
    <>
      <Tooltip title={tooltip} placement="left">
        <IconButton
          onClick={handleOpenModal}
          sx={{
            ...getSizeStyles(),
            ...getVariantStyles(),
            borderRadius: variant === 'floating' ? '50%' : '4px',
          }}
          aria-label="Help"
        >
          <QuestionIcon />
        </IconButton>
      </Tooltip>
      
      <HelpModal
        open={isModalOpen}
        onClose={handleCloseModal}
        videoUrl={videoUrl}
        title={title}
        description={description}
        enableSupportSection={enableSupportSection}
      />
    </>
  );
};

export default HelpButton;
